import React, { useCallback, useEffect, useState } from 'react';
import { Box, Divider, FormControl, FormControlLabel, RadioGroup, Typography } from '@mui/material';
import { capitalize, isEmpty } from 'lodash';
import { MAX_DROPDOWN_OPTIONS, PICK_ANSWER_TYPE, QUESTION_TYPE_CHOICES } from 'common/constants/firms';
import { Option } from 'common/types/firm';
import { useTasks } from 'services/hooks';
import { resolveQuestionTypeValue } from 'services/hooks/useRequestDocsAndQuestions/questionTypeUtilities';
import { SubQuestion } from 'services/hooks/useRequestDocsAndQuestions/types';
import AddSubQuestionButton from './AddSubQuestionButton';
import DropdownOptions from './DropdownOptions';
import ModalItemBox from './ModalItemBox';
import { StyledRadio } from './styles';
import { QuestionContentProps } from './types';

const QuestionContent = ({
  formValues,
  setFormValues,
  setIsMaxCharactersError,
  dropdownTypeValue,
  isEdit,
}: QuestionContentProps) => {
  const selectedQuestionType = resolveQuestionTypeValue(formValues.question_type);
  const subQuestion = formValues.children_questions[0] || { name: '', description: '' };
  const isMaxOptions = formValues.options.length >= MAX_DROPDOWN_OPTIONS;
  const { getQuestionOptions } = useTasks();
  const [questionOptions, setQuestionOptions] = useState<Option[]>([]);

  useEffect(() => {
    let isMounted = true;

    getQuestionOptions().then(response => {
      if (!isMounted || !Array.isArray(response)) {
        return;
      }

      setQuestionOptions(response);
    });

    return () => {
      isMounted = false;
    };
  }, [getQuestionOptions]);

  const handleInputSubQuestionChange = useCallback(
    (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
      const newSubQuestions = [...formValues.children_questions];
      const key = e.target.name as keyof SubQuestion;
      newSubQuestions[index] = {
        ...newSubQuestions[index],
        [key]: e.target.value,
      };

      setFormValues({
        ...formValues,
        children_questions: newSubQuestions,
      });
    },
    [formValues, setFormValues]
  );

  const handleAnswerTypeClick = useCallback(
    (value: string) => {
      setFormValues({
        ...formValues,
        question_type: Number(value),
      });
    },
    [setFormValues, formValues]
  );

  return (
    <>
      <Box mb={1}>
        <Typography variant="body1" gutterBottom style={{ fontWeight: 700 }}>
          {PICK_ANSWER_TYPE}
        </Typography>
        <FormControl>
          <RadioGroup
            row
            aria-labelledby="pick-answer-type-radio-label"
            name="radio-buttons-group"
            value={selectedQuestionType}
            onChange={event => handleAnswerTypeClick(event.target.value)}>
            {QUESTION_TYPE_CHOICES.map(type => (
              <FormControlLabel
                key={type.value}
                value={type.value}
                control={<StyledRadio />}
                label={capitalize(type.label)}
              />
            ))}
          </RadioGroup>
        </FormControl>
      </Box>

      {selectedQuestionType === dropdownTypeValue && (
        <Box>
          <DropdownOptions
            formValues={formValues}
            setFormValues={setFormValues}
            isMaxOptions={isMaxOptions}
            questionOptions={questionOptions}
          />
        </Box>
      )}

      <Divider style={{ marginTop: 8 }} />

      <AddSubQuestionButton
        setFormValues={setFormValues}
        formValues={formValues}
        isEdit={isEdit}
        subQuestion={subQuestion}
      />
      {!isEmpty(formValues.children_questions) && (
        <ModalItemBox
          key={subQuestion.question_type ?? 0}
          index={0}
          item={subQuestion}
          handleInputChange={handleInputSubQuestionChange}
          setIsMaxCharactersError={setIsMaxCharactersError}
          formValues={formValues}
          setFormValues={setFormValues}
          attribute="children_questions"
          inputLabel="Sub-question"
          additionalInputs={[
            {
              id: 'description',
              name: 'description',
              label: 'Description',
              placeholder: 'Description',
              maxCharacters: 500,
            },
          ]}
        />
      )}
    </>
  );
};

export default QuestionContent;
